import { config } from 'config';
import { configurationActions } from 'core/Configuration';
import isEmpty from 'lodash/isEmpty';
import { useDropzone } from 'react-dropzone';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    AlertModal,
    ErrorMessage,
    PanelSection
} from 'views/components';
import {
    BtnContainer,
    Container,
    Content,
    DropContainer,
    IconContainer,
    Layout
} from './styled';
import {
    Card,
    Checkbox,
    ExpandButton,
    FormGroup,
    Input,
    Loader,
    MaterialIcon,
    SelectSearch
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import React, { useCallback, useEffect, useState } from 'react';
import {
    useCheckbox,
    useDispatch,
    useInput,
    useObjectState,
    useSelector
} from 'hooks';

const {
    allowGlobalCallRates,
    recipesFiles,
    recording
} = config;

const GeneralConfigurations = () => {

    const { primaryColor } = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        configurations,
        error,
        result,
        uploading,
        uploadingLogo,
        uploadingLogoError,
        uploadingLogoProcessing
    } = useSelector('configuration');

    const fileOptions = [{
        label: t('Importar drogas'),
        value: 'import/drug'
    }, {
        label: t('Importar productos'),
        value: 'import/product'
    }, {
        label: t('Actualizar productos'),
        value: 'update/product'
    }];

    const [callRates, setCallRates, setCallRate] = useObjectState({});
    const [loaded, setLoaded] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [recordCheckBox, recordEnabled, setRecordEnabled] = useCheckbox(false);
    const [file, setFile] = useState({});
    const [fileInput, selectedFileOption] = useInput('import/drug');
    const [logoFile, setLogoFile] = useState({});
    const [showModal, setShowModal] = useState(false);

    const onDrop = useCallback(acceptedFiles => {
        setFile(acceptedFiles[0]);
    }, []);

    const onLogoDrop = useCallback(acceptedFiles => {
        setLogoFile(acceptedFiles[0]);
    }, []);

    const { getRootProps, getInputProps } = useDropzone({ onDrop });
    const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps } = useDropzone({ onDrop: onLogoDrop });

    useEffect(() => {
        dispatch(configurationActions.getConfigurations());
        !!uploadingLogoError && dispatch(configurationActions.setConfigurationUploadingLogoError(''));
    }, []);

    useEffect(() => {
        if (!uploadingLogoProcessing) return;
        !!uploadingLogoError && dispatch(configurationActions.setConfigurationUploadingLogoError(''));
    }, [uploadingLogoProcessing]);

    useEffect(() => {
        if (!isEmpty(configurations)) {
            const {
                appointment_rate,
                call_rate,
                first_call_rate,
                record
            } = configurations;
            if (!loaded) {
                setLoaded(true);
                setRecordEnabled(record);
                setCallRates({
                    appointment_rate,
                    call_rate,
                    first_call_rate
                });
            } else {
                if (result) {
                    setModalMessage(t('La configuración se ha actualizado correctamente'));
                    setRecordEnabled(record);
                    setShowModal(true);
                } else {
                    if (error) {
                        setModalMessage(error);
                        setRecordEnabled(record);
                        setShowModal(true);
                    }
                }
            }
        }
    }, [configurations, error, loaded, result, setCallRates, setRecordEnabled]);

    const handleCloseModal = () => {
        if (error) {
            setError(false);
        }
        if (result) {
            dispatch(configurationActions.setConfigurationResult(false));
        }
        setShowModal(false);
    };

    const handleSave = () => {
        const newConfiguration = {
            ...(!!recording && { record: recordEnabled ? 1 : 0 }),
            ...(!!allowGlobalCallRates && callRates)
        };
        dispatch(configurationActions.updateGlobalConfig(newConfiguration));
    };

    const onUpload = () => {
        if (!isEmpty(file)) {
            dispatch(configurationActions.uploadFile({
                file,
                option: selectedFileOption
            }));
        }
    };

    const onUploadLogo = () => {
        if (!isEmpty(logoFile)) {
            dispatch(configurationActions.uploadLogoFile({
                file: logoFile
            }));
        }
    };

    return (
        <Layout>
            <Container>
                <Content>
                    {
                        recording &&
                        <Card>
                            <PanelSection title={ t('Grabación de llamadas') }>
                                <Checkbox
                                    { ...recordCheckBox }
                                    label={ t('Grabar y almacenar llamadas realizadas en la plataforma') }
                                />
                            </PanelSection>
                        </Card>
                    }
                    {
                        recipesFiles &&
                        <>
                            <Card>
                                <PanelSection title={ t('Archivos para recetas') }>
                                    <Row>
                                        <Col md="4" xs="12">
                                            <SelectSearch
                                                { ...fileInput }
                                                height="55px"
                                                margin="0px"
                                                options={ fileOptions }
                                                width="100%"
                                            />
                                        </Col>
                                        <Col md="4" xs="10">
                                            <DropContainer { ...getRootProps() }>
                                                <input { ...getInputProps() } />
                                                {
                                                    isEmpty(file)
                                                        ? t('Arrastra el archivo o haz click')
                                                        : file.name
                                                }
                                            </DropContainer>
                                        </Col>
                                        <Col md="1" xs="2">
                                            {
                                                !uploading
                                                    ? <IconContainer onClick={ onUpload }>
                                                        <MaterialIcon color={ primaryColor } icon="cloud_upload" size={ 58 } />
                                                    </IconContainer>
                                                    : <Loader active={ true } size={ 20 } type="dots" />
                                            }
                                        </Col>
                                    </Row>
                                </PanelSection>
                            </Card>
                            <Card>
                                <PanelSection title={ t('Logo de recetas') }>
                                    <Row>
                                        <Col md="4" xs="10">
                                            <DropContainer { ...getLogoRootProps() }>
                                                <input { ...getLogoInputProps() } />
                                                {
                                                    isEmpty(logoFile)
                                                        ? t('Arrastra el archivo o haz click')
                                                        : logoFile.name
                                                }
                                            </DropContainer>
                                        </Col>
                                        <Col md="1" xs="2">
                                            {
                                                !uploadingLogo
                                                    ? <IconContainer onClick={ onUploadLogo }>
                                                        <MaterialIcon color={ primaryColor } icon="cloud_upload" size={ 58 } />
                                                    </IconContainer>
                                                    : <Loader active={ true } size={ 20 } type="dots" />
                                            }
                                        </Col>
                                    </Row>
                                    {
                                        !isEmpty(uploadingLogoError) &&
                                        <Row>
                                            <Col xs="12">
                                                <ErrorMessage relative error={ uploadingLogoError } margin="10px 0px" />
                                            </Col>
                                        </Row>
                                    }
                                </PanelSection>
                            </Card>
                        </>
                    }
                    {
                        allowGlobalCallRates &&
                        <Card>
                            <PanelSection title={ t('Tarifario de llamadas por defecto') }>
                                <Row>
                                    <Col md="4" xs="12">
                                        <FormGroup label={ t('Directa') }>
                                            <Input
                                                type="number"
                                                value={ callRates.call_rate ?? '' }
                                                onChange={ value => setCallRate('call_rate', value) }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4" xs="12">
                                        <FormGroup label={ t('Turnos') }>
                                            <Input
                                                type="number"
                                                value={ callRates.appointment_rate ?? '' }
                                                onChange={ value => setCallRate('appointment_rate', value) }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4" xs="12">
                                        <FormGroup label={ t('Primer turno') }>
                                            <Input
                                                type="number"
                                                value={ callRates.first_call_rate ?? '' }
                                                onChange={ value => setCallRate('first_call_rate', value) }
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </PanelSection>
                        </Card>
                    }
                </Content>
                <BtnContainer>
                    <ExpandButton
                        caption={ t('Confirmar') }
                        size={ 150 }
                        onClick={ handleSave }
                    />
                </BtnContainer>
            </Container>
            <AlertModal
                active={ showModal }
                message={ modalMessage }
                onToggle={ handleCloseModal }
            />
        </Layout>
    );
};

GeneralConfigurations.propTypes = {
    
};

export default GeneralConfigurations;
